import React, { useEffect, useState, useMemo } from 'react';
import { getFirestore, collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import PlayerDetailView from './PlayerDetailView';
import Podium from './Podium';

const PlayerPerformanceScreen = ({ onBack, onHome, onLogout }) => {
    const [allGames, setAllGames] = useState([]);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('winRate'); // Default to winRate for better podium
    const appId = "1:699575750232:web:8d7ebbfd7a7bb5c866842d";

    const auth = getAuth();
    const user = auth.currentUser;

    // Load games
    useEffect(() => {
        if (user) {
            const db = getFirestore();
            const getGames = async () => {
                const gamesCollection = collection(db, "personalData", appId, "users", user.uid, "games");
                const gamesSnapshot = await getDocs(gamesCollection);
                const gamesData = gamesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // Only process completed games
                const completedGames = gamesData.filter(game => game.handHistory && game.handHistory.length === 13);
                setAllGames(completedGames);
            };
            getGames();
        }
    }, [appId, user]);

    const playerList = useMemo(() => {
        const players = {};
        // Sort games chronologically for streak calculation
        const sortedGames = [...allGames].sort((a, b) => new Date(a.date) - new Date(b.date));

        sortedGames.forEach(game => {
            // Initialize players from game summary
            game.players.forEach(p => {
                if (!players[p.name]) {
                    players[p.name] = {
                        name: p.name,
                        gamesPlayed: 0,
                        wins: 0,
                        totalScore: 0,
                        lastPlayed: null,
                        totalBids: 0,
                        totalWon: 0,
                        perfectBids: 0,
                        totalHands: 0,

                        recentResults: [] // Track last few games for streak
                    };
                }
                players[p.name].gamesPlayed++;
                players[p.name].totalScore += p.score;

                // Check for wins
                let isWin = false;
                if (game.winners && game.winners.length > 0) {
                    if (game.winners.some(w => w.name === p.name)) isWin = true;
                } else if (game.winner && game.winner.name === p.name) {
                    isWin = true;
                }

                if (isWin) players[p.name].wins++;
                players[p.name].recentResults.push(isWin ? 'W' : 'L');

                // Track last played date
                const gameDate = new Date(game.date);
                if (!players[p.name].lastPlayed || gameDate > players[p.name].lastPlayed) {
                    players[p.name].lastPlayed = gameDate;
                }
            });

            // Calculate bids and won from hand history
            if (game.handHistory) {
                game.handHistory.forEach(hand => {
                    if (hand.players) {
                        Object.entries(hand.players).forEach(([playerName, playerData]) => {
                            if (players[playerName]) {
                                const bid = Number(playerData.bid) || 0;
                                const won = playerData.won || 0;
                                players[playerName].totalBids += bid;
                                players[playerName].totalWon += won;
                                players[playerName].totalHands++;

                                // Track Perfect Bids
                                if (won === bid) players[playerName].perfectBids++;
                            }
                        });
                    }
                });
            }
        });

        let processedPlayers = Object.values(players).map(p => {
            // Calculate Risk Level
            let riskLevel = 50;
            if (p.totalWon > 0) {
                const ratio = p.totalBids / p.totalWon;
                if (ratio >= 1) {
                    riskLevel = 85 + ((ratio - 1) / 0.2) * 15;
                } else {
                    riskLevel = ratio * 85;
                }
                riskLevel = Math.min(100, Math.max(0, Math.round(riskLevel)));
            } else if (p.totalBids > 0) {
                riskLevel = 100;
            } else {
                riskLevel = 0;
            }

            // Calculate Streak
            let streak = 'neutral';
            const recent = p.recentResults.slice(-3); // Last 3 games
            if (recent.length >= 3 && recent.every(r => r === 'W')) streak = 'hot';
            else if (recent.length >= 3 && recent.every(r => r === 'L')) streak = 'cold';

            // Calculate Luck Rating
            // Based solely on Perfect Bids (getting exactly what you bid)
            let luckRating = 0;
            if (p.totalHands > 0) {
                luckRating = (p.perfectBids / p.totalHands) * 100;
            }
            luckRating = Math.round(luckRating);

            return {
                ...p,
                winRate: p.gamesPlayed > 0 ? ((p.wins / p.gamesPlayed) * 100).toFixed(0) : 0,
                avgScore: p.gamesPlayed > 0 ? (p.totalScore / p.gamesPlayed).toFixed(0) : 0,
                riskLevel,
                luckRating,
                streak
            };
        });

        // Filter
        if (searchTerm) {
            processedPlayers = processedPlayers.filter(p =>
                p.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort
        processedPlayers.sort((a, b) => {
            if (sortBy === 'name') return a.name.localeCompare(b.name);
            if (sortBy === 'gamesPlayed') {
                if (b.gamesPlayed !== a.gamesPlayed) return b.gamesPlayed - a.gamesPlayed;
                return a.name.localeCompare(b.name);
            }
            if (sortBy === 'winRate') {
                if (Number(b.winRate) !== Number(a.winRate)) return Number(b.winRate) - Number(a.winRate);
                return a.name.localeCompare(b.name);
            }
            if (sortBy === 'highRisk') {
                if (b.riskLevel !== a.riskLevel) return b.riskLevel - a.riskLevel;
                return a.name.localeCompare(b.name);
            }
            if (sortBy === 'sensible') {
                if (a.riskLevel !== b.riskLevel) return a.riskLevel - b.riskLevel;
                return a.name.localeCompare(b.name);
            }
            if (sortBy === 'sensible') {
                if (a.riskLevel !== b.riskLevel) return a.riskLevel - b.riskLevel;
                return a.name.localeCompare(b.name);
            }
            if (sortBy === 'luckiest') {
                if (b.luckRating !== a.luckRating) return b.luckRating - a.luckRating;
                return a.name.localeCompare(b.name);
            }
            return 0;
        });

        return processedPlayers;
    }, [allGames, searchTerm, sortBy]);

    // Identify current user's player profile (simple matching by email/name if possible, or just first in list for now if no match)
    // Ideally, we'd match user.email to a player name, but we don't strictly enforce that link yet.
    // For now, we'll try to match by name if the user has set a display name, otherwise we might need a manual "This is me" selector later.
    // As a fallback, we won't show the sticky bar if we can't confidently identify the user, OR we show the top player.
    // Let's assume for this "Market Capture" redesign, we want to show the user's rank. 
    // Since we don't have a strict link, let's skip the auto-match for now and just show the "My Rank" bar if we can find a player with the same name as the user's email (before @).
    const userPlayer = useMemo(() => {
        if (!user || !user.email) return null;
        const nameFromEmail = user.email.split('@')[0];
        return playerList.find(p => p.name.toLowerCase() === nameFromEmail.toLowerCase());
    }, [playerList, user]);


    const handleDeletePlayer = async (playerName) => {
        if (!user) return;

        const db = getFirestore();
        const gamesToDelete = allGames.filter(game =>
            game.players.some(p => p.name === playerName)
        );

        for (const game of gamesToDelete) {
            const gameRef = doc(db, "personalData", appId, "users", user.uid, "games", game.id);
            await deleteDoc(gameRef);
        }

        const gamesCollection = collection(db, "personalData", appId, "users", user.uid, "games");
        const gamesSnapshot = await getDocs(gamesCollection);
        const gamesData = gamesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllGames(gamesData);
        setDeleteConfirm(null);
    };

    if (selectedPlayer) {
        return <PlayerDetailView player={selectedPlayer} allGames={allGames} onBack={() => setSelectedPlayer(null)} onHome={onHome} />;
    }

    const top3 = playerList.slice(0, 3);
    const restOfPlayers = playerList.slice(3);

    return (
        <div className="h-[100dvh] bg-[#0f172a] text-white font-['Inter'] flex flex-col items-center relative overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>
            <div className="absolute top-[-10%] left-[-10%] w-[40vw] h-[40vw] bg-purple-600/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40vw] h-[40vw] bg-amber-600/20 rounded-full blur-[120px] pointer-events-none" />

            <header className="relative z-20 w-full max-w-7xl p-4 md:p-6 flex flex-col gap-3 md:gap-6 bg-[#0f172a]/90 backdrop-blur-xl shadow-2xl border-b border-white/5">
                <div className="flex items-center justify-between">
                    <button
                        onClick={onBack}
                        className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                    >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <h1 className="text-xl md:text-4xl font-black tracking-tight font-['Roboto_Slab'] text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-600 text-center uppercase">
                        Leaderboard
                    </h1>

                    <div className="flex items-center gap-2">
                        <button onClick={onHome} className="p-2 md:p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all">
                            <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>
                        </button>
                        <button onClick={onLogout} className="p-2 md:p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        </button>
                    </div>
                </div>

                {/* Controls Bar */}
                <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-center justify-between bg-white/5 p-3 md:p-4 rounded-2xl border border-white/10 backdrop-blur-md">
                    <div className="relative w-full md:w-96">
                        <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search players..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl py-2.5 md:py-3 pl-10 md:pl-12 pr-4 text-sm md:text-base text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/50 transition-all"
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto no-scrollbar">
                        <span className="text-sm text-gray-400 whitespace-nowrap">Sort by:</span>
                        {['winRate', 'highRisk', 'sensible', 'luckiest', 'gamesPlayed'].map((key) => (
                            <button
                                key={key}
                                onClick={() => setSortBy(key)}
                                className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-bold transition-all whitespace-nowrap flex-shrink-0 ${sortBy === key
                                    ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/25'
                                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                                    }`}
                            >
                                {key === 'gamesPlayed' ? 'Most Games' :
                                    key === 'winRate' ? 'Best Win %' :
                                        key === 'highRisk' ? 'Risk Taker' :
                                            key === 'sensible' ? 'Sensible Player' :
                                                key === 'luckiest' ? 'Luckiest Player' : 'Name'}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Sort Description */}
                <div className="px-4 md:px-6 py-2 text-center">
                    <p className="text-xs text-gray-400">
                        {sortBy === 'highRisk' && "Risk Taker: Players who bid aggressively relative to their wins (High Bid/Won Ratio)."}
                        {sortBy === 'sensible' && "Sensible Player: Players who bid conservatively and minimize risk (High Safety Rating)."}
                        {sortBy === 'luckiest' && "Luckiest Player: Players with high rate of Perfect Bids (getting exactly what they bid)."}
                        {sortBy === 'winRate' && "Best Win %: Percentage of games won."}
                        {sortBy === 'gamesPlayed' && "Most Games: Total number of completed games."}
                    </p>
                </div>
            </header>

            <main className="relative z-10 w-full max-w-7xl flex-1 overflow-hidden px-4 md:px-6 pb-24 min-h-0 flex flex-col">
                {playerList.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                        <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mb-6">
                            <span className="text-4xl">👥</span>
                        </div>
                        <p className="text-xl font-bold text-gray-300">No players found</p>
                        <p className="text-sm text-gray-500 mt-2">Try adjusting your search or play some games!</p>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        {/* Podium Section */}
                        {!searchTerm && (sortBy === 'winRate' || sortBy === 'gamesPlayed' || sortBy === 'highRisk' || sortBy === 'sensible' || sortBy === 'luckiest') && (
                            <Podium topPlayers={top3} onPlayerClick={setSelectedPlayer} sortBy={sortBy} />
                        )}

                        {/* List Section */}
                        <div className="flex flex-col gap-3 pb-6">
                            {(!searchTerm && (sortBy === 'winRate' || sortBy === 'gamesPlayed' || sortBy === 'highRisk' || sortBy === 'sensible' || sortBy === 'luckiest') ? restOfPlayers : playerList).map((player, index) => {
                                // Calculate actual rank based on full list
                                const actualRank = (!searchTerm && (sortBy === 'winRate' || sortBy === 'gamesPlayed' || sortBy === 'highRisk' || sortBy === 'sensible' || sortBy === 'luckiest')) ? index + 4 : index + 1;

                                return (
                                    <div
                                        key={player.name}
                                        onClick={() => setSelectedPlayer(player)}
                                        className="group relative bg-[#1e293b]/60 backdrop-blur-md rounded-xl border border-white/10 p-3 hover:border-amber-500/50 hover:bg-[#1e293b]/80 transition-all duration-200 cursor-pointer flex items-center gap-4"
                                    >
                                        <div className="w-8 text-center font-black text-gray-500 text-lg">
                                            {actualRank}
                                        </div>

                                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-[#1e293b] font-black text-sm shadow-lg shadow-amber-500/20 flex-shrink-0 relative">
                                            {player.name.substring(0, 2).toUpperCase()}
                                            {player.streak === 'hot' && (
                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center border border-[#1e293b]">
                                                    <span className="text-[8px]">🔥</span>
                                                </div>
                                            )}
                                            {player.streak === 'cold' && (
                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center border border-[#1e293b]">
                                                    <span className="text-[8px]">❄️</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-base font-bold text-white group-hover:text-amber-400 transition-colors truncate">
                                                    {player.name}
                                                </h3>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-gray-400">
                                                <span>{player.gamesPlayed} Games</span>
                                                <span className="w-1 h-1 rounded-full bg-gray-600" />
                                                <span className={`${Number(player.winRate) >= 50 ? 'text-green-400' : ''}`}>{player.winRate}% Win Rate</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <div className="text-right hidden sm:block">
                                                {sortBy === 'highRisk' ? (
                                                    <>
                                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Risk Level</p>
                                                        <div className="flex items-center justify-end gap-2">
                                                            <p className={`font-bold ${player.riskLevel >= 75 ? 'text-red-400' : player.riskLevel >= 50 ? 'text-yellow-400' : 'text-green-400'}`}>
                                                                {player.riskLevel}%
                                                            </p>
                                                            <span className="text-[10px] text-gray-500">
                                                                ({(player.totalBids / (player.totalWon || 1)).toFixed(1)}x)
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : sortBy === 'sensible' ? (
                                                    <>
                                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Safety Rating</p>
                                                        <div className="flex items-center justify-end gap-2">
                                                            <p className={`font-bold ${player.riskLevel <= 30 ? 'text-blue-400' : player.riskLevel <= 50 ? 'text-green-400' : 'text-yellow-400'}`}>
                                                                {100 - player.riskLevel}%
                                                            </p>
                                                            <span className="text-[10px] text-gray-500">
                                                                ({(player.totalBids / (player.totalWon || 1)).toFixed(1)}x)
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : sortBy === 'luckiest' ? (
                                                    <>
                                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Luck Rating</p>
                                                        <div className="flex items-center justify-end gap-2">
                                                            <p className={`font-bold ${player.luckRating >= 80 ? 'text-purple-400' : player.luckRating >= 50 ? 'text-blue-400' : 'text-gray-400'}`}>
                                                                {player.luckRating}%
                                                            </p>
                                                            <span className="text-[10px] text-gray-500">
                                                                ({player.perfectBids}/{player.totalHands} Perfect)
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Avg Score</p>
                                                        <p className="font-bold text-white">{player.avgScore}</p>
                                                    </>
                                                )}
                                            </div>

                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDeleteConfirm(player.name);
                                                }}
                                                className="p-2 rounded-lg hover:bg-red-500/20 text-gray-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </main>

            {/* Sticky User Rank Bar */}
            {userPlayer && (
                <div className="absolute bottom-0 left-0 right-0 z-30 bg-[#1e293b] border-t border-amber-500/30 p-4 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
                    <div className="max-w-7xl mx-auto flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center text-[#1e293b] font-black shadow-lg shadow-amber-500/20">
                                {userPlayer.name.substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                                <p className="text-xs text-amber-400 font-bold uppercase tracking-wider">Your Rank</p>
                                <p className="text-lg font-black text-white">
                                    #{playerList.findIndex(p => p.name === userPlayer.name) + 1}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="text-right">
                                <p className="text-xs text-gray-400">Win Rate</p>
                                <p className="font-bold text-white">{userPlayer.winRate}%</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-400">Games</p>
                                <p className="font-bold text-white">{userPlayer.gamesPlayed}</p>
                            </div>
                            <button
                                onClick={() => setSelectedPlayer(userPlayer)}
                                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-bold transition-colors"
                            >
                                View Stats
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="w-full max-w-md rounded-3xl bg-[#1e293b] border border-white/10 shadow-2xl p-8 transform transition-all scale-100">
                        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-white text-center mb-2">Delete Player?</h2>
                        <p className="text-gray-400 text-center mb-8">
                            Are you sure you want to delete <span className="font-bold text-white">{deleteConfirm}</span>?
                            This will permanently remove all <span className="text-amber-400 font-bold">{allGames.filter(g => g.players.some(p => p.name === deleteConfirm)).length}</span> games they participated in.
                        </p>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="flex-1 px-6 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white font-bold hover:bg-white/10 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDeletePlayer(deleteConfirm)}
                                className="flex-1 px-6 py-3.5 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 shadow-lg shadow-red-500/25 transition-all"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlayerPerformanceScreen;
